import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useChallenge } from "@/hooks/useChallenge";
import { useStravaConnection } from "@/hooks/useStravaConnection";
import { useStravaConnectionResult } from "@/hooks/useStravaConnectionResult";
import { DiscoverChallengeCard } from "@/components/DiscoverChallengeCard";
import { ConnectStravaPrompt } from "@/components/ConnectStravaPrompt";
import { StickyHeader } from "@/components/ui/StickyHeader";
// import { HeaderIconButton } from "@/components/ui/HeaderIconButton";
import { getFirstSentence, getChallengeIcon } from "@/utils/challengeHelpers";
import { MOCK_RUN_CHALLENGES } from "@/data/mockChallenges";

export function Challenges() {
  const navigate = useNavigate();
  const {
    challenges,
    fetchChallenges,
    isLoading: isChallengesLoading,
    error,
  } = useChallenge();
  const { showPrompt, ensureConnected, closePrompt, handleConnectionSuccess } =
    useStravaConnection();

  // Handle Strava OAuth callback result (shows toast, cleans URL)
  useStravaConnectionResult();

  const [selectedCategory, setSelectedCategory] = useState("Trending");

  const categories = ["Trending", "Running"];

  // Fetch challenges when component mounts
  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  // Filter and sort challenges based on category
  const displayChallenges = useMemo(() => {
    // If we have real challenges from API, use them
    if (challenges && challenges.length > 0) {
      let filtered = challenges;

      // Filter by category if not "Trending"
      if (selectedCategory !== "Trending") {
        const typeMap: Record<string, string> = {
          Running: "run",
        };
        const type = typeMap[selectedCategory];
        if (type) {
          filtered = challenges.filter((c) => c.type === type);
        }
      }

      // Sort by participant count (trending logic)
      filtered.sort(
        (a, b) => (b.participantCount || 0) - (a.participantCount || 0)
      );

      return filtered;
    }

    // Return null to indicate we should use mock data
    return null;
  }, [challenges, selectedCategory]);

  const handleCreateChallenge = () => {
    // Check if Strava is connected before allowing create
    ensureConnected(() => {
      navigate("/create-challenge");
    });
  };

  const handleChallengeClick = (challengeId: string) => {
    navigate(`/challenge-preview/${challengeId}`);
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark">
      <StickyHeader
        title="Discover"
        // rightContent={
        //   <HeaderIconButton
        //     icon="notifications"
        //     variant="subtle"
        //     ariaLabel="Notifications"
        //   />
        // }
      >
        {/* Search Bar */}
        <div className="relative px-4 pb-4">
          <span className="material-symbols-outlined absolute left-8 top-1/2 -translate-y-1/2 text-text-light/50 dark:text-text-dark/50">
            search
          </span>
          <input
            className="w-full h-14 rounded-full border-none bg-secondary-light dark:bg-secondary-dark pl-12 pr-4 text-text-light dark:text-text-dark placeholder:text-text-light/50 dark:placeholder:text-text-dark/50 focus:ring-2 focus:ring-primary/50"
            placeholder="Search challenges..."
            type="text"
          />
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 px-6 pb-4 overflow-x-auto whitespace-nowrap scrollbar-hide">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`flex h-10 shrink-0 items-center justify-center gap-x-2 rounded-full px-4 ${
                selectedCategory === category
                  ? "bg-primary text-black"
                  : "bg-secondary-light dark:bg-secondary-dark text-text-light dark:text-text-dark"
              }`}
            >
              <p
                className={`text-sm ${
                  selectedCategory === category ? "font-bold" : "font-medium"
                } leading-normal`}
              >
                {category}
              </p>
            </button>
          ))}
        </div>
      </StickyHeader>

      {/* Main Content */}
      <main className="flex-1 pb-24">
        {/* Challenge Cards */}
        <div className="flex flex-col gap-4 px-6">
          {isChallengesLoading ? (
            // Loading State
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            // Error State
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={() => fetchChallenges()}
                className="text-primary underline hover:opacity-80"
              >
                Retry
              </button>
            </div>
          ) : (
            // Show mock run challenges + real challenges from database
            <>
              {/* Mock Run Challenges - Always show to indicate activity */}
              {MOCK_RUN_CHALLENGES.map((challenge) => (
                <DiscoverChallengeCard
                  key={challenge.id}
                  title={challenge.name}
                  description={getFirstSentence(challenge.description)}
                  icon={getChallengeIcon(challenge.type)}
                  duration={`${challenge.durationDays} Days`}
                  stake={`$${challenge.stakeAmount}`}
                  players={`${challenge.participantCount}`}
                  onClick={() => handleChallengeClick(challenge.id)}
                />
              ))}

              {/* Real Challenges from Database */}
              {displayChallenges &&
                displayChallenges.map((challenge) => (
                  <DiscoverChallengeCard
                    key={challenge.id}
                    title={challenge.name}
                    description={getFirstSentence(challenge.description)}
                    icon={getChallengeIcon(challenge.type)}
                    duration={`${challenge.durationDays} Days`}
                    stake={`$${challenge.stakeAmount}`}
                    players={`${
                      challenge.participantCount ||
                      challenge.participants?.length ||
                      0
                    }`}
                    onClick={() => handleChallengeClick(challenge.id)}
                  />
                ))}
            </>
          )}
        </div>
      </main>

      {/* Floating Action Button */}
      <div className="fixed bottom-26 right-6 z-20">
        <button
          onClick={handleCreateChallenge}
          className="flex h-14 w-14 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-primary text-black shadow-lg"
        >
          <span className="material-symbols-outlined text-3xl">add</span>
        </button>
      </div>

      {/* Strava Connection Prompt */}
      <ConnectStravaPrompt
        isOpen={showPrompt}
        onClose={closePrompt}
        onSuccess={handleConnectionSuccess}
      />
    </div>
  );
}
